import { useEffect, useReducer } from 'react';
import { getActiveMembers } from '../../api/payment/membership.api';

/**
 * Tra "có phải hội viên kênh không" theo cặp (creatorId, userId) cho toàn app.
 * Nhiều chỗ render tên cùng gọi useIsMember → gom trong 60 ms, gọi theo từng creatorId,
 * mỗi lô tối đa 100 id, nhớ kết quả 60 giây. Lỗi mạng coi như không phải hội viên và chỉ nhớ 5 giây để thử lại sớm.
 */
const BATCH_MS = 60;
const MAX_BATCH = 100;
const TTL_MS = 60_000;
const FAILED_TTL_MS = 5_000;

const cache = new Map<string, { value: boolean; at: number; ttl: number }>();
const inflight = new Set<string>();
const pending = new Map<string, Set<string>>();
const subscribers = new Set<() => void>();
let timer: ReturnType<typeof setTimeout> | null = null;

const keyOf = (creatorId: string, userId: string) => `${creatorId}:${userId}`;
const notify = () =>
  subscribers.forEach((fn) => {
    // Một nơi nhận lỗi không được làm các nơi còn lại bỏ lỡ cập nhật.
    try {
      fn();
    } catch {
      /* bỏ qua */
    }
  });

const fresh = (key: string) => {
  const hit = cache.get(key);
  return !!hit && Date.now() - hit.at < hit.ttl;
};

const flush = () => {
  timer = null;
  const groups = Array.from(pending.entries());
  pending.clear();
  groups.forEach(([creatorId, set]) => {
    const ids = Array.from(set);
    for (let i = 0; i < ids.length; i += MAX_BATCH) {
      const chunk = ids.slice(i, i + MAX_BATCH);
      const done = (members: string[], failed = false) => {
        const yes = new Set(members);
        chunk.forEach((id) => {
          const k = keyOf(creatorId, id);
          inflight.delete(k);
          cache.set(k, {
            value: yes.has(id),
            at: Date.now(),
            ttl: failed ? FAILED_TTL_MS : TTL_MS,
          });
        });
        notify();
      };
      getActiveMembers(creatorId, chunk).then(
        (list) => done(list || []),
        () => done([], true),
      );
    }
  });
};

const request = (creatorId?: string, userId?: string) => {
  if (!creatorId || !userId) return;
  const k = keyOf(creatorId, userId);
  if (fresh(k) || inflight.has(k)) return;
  inflight.add(k);
  let set = pending.get(creatorId);
  if (!set) {
    set = new Set();
    pending.set(creatorId, set);
  }
  set.add(userId);
  if (!timer) timer = setTimeout(flush, BATCH_MS);
};

const subscribe = (fn: () => void) => {
  subscribers.add(fn);
  return () => {
    subscribers.delete(fn);
  };
};

/** Người này có đang là hội viên còn hạn của kênh này không (false khi chưa biết). */
export const useIsMember = (creatorId?: string, userId?: string): boolean => {
  const [, force] = useReducer((x: number) => x + 1, 0);
  useEffect(() => {
    request(creatorId, userId);
    return subscribe(() => force());
  }, [creatorId, userId]);
  return creatorId && userId
    ? cache.get(keyOf(creatorId, userId))?.value ?? false
    : false;
};

export const __subscribeForTest = subscribe;

export const __resetMemberStoreForTest = () => {
  cache.clear();
  inflight.clear();
  pending.clear();
  subscribers.clear();
  if (timer) clearTimeout(timer);
  timer = null;
};
